import { CameraIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { stashOcrFieldsForFeedback } from "components/feedback/business-card-scan-feedback-store";
import { Content, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
type SupportedMimeType = (typeof SUPPORTED_MIME_TYPES)[number];

const isSupportedMimeType = (value: string): value is SupportedMimeType =>
    (SUPPORTED_MIME_TYPES as readonly string[]).includes(value);

const looksLikeHeic = (file: File): boolean => {
    const lowerName = file.name.toLowerCase();
    const lowerType = file.type.toLowerCase();
    return (
        lowerType === "image/heic" ||
        lowerType === "image/heif" ||
        lowerName.endsWith(".heic") ||
        lowerName.endsWith(".heif")
    );
};

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error("read error"));
        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string") {
                reject(new Error("unexpected read result"));
                return;
            }
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(file);
    });

export const ScanBusinessCardPage: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const regionId = useRegionId();
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const scanMutation = useMutation(trpcClient.businessCardScan.scan.mutationOptions());

    const handleFileSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const selected = event.target.files?.[0];
            event.target.value = "";
            if (!selected) {
                return;
            }

            if (looksLikeHeic(selected)) {
                setFile(null);
                setFileError(
                    'iPhone HEIC photos aren\'t supported yet. Tap "Take photo" to use the camera, or re-save the image as JPEG.',
                );
                return;
            }

            if (!isSupportedMimeType(selected.type)) {
                setFile(null);
                setFileError(
                    "That file type isn't supported. Please pick a JPEG, PNG, or WebP image.",
                );
                return;
            }

            setFileError(null);
            setFile(selected);
            handleEvent("scan-business-card-photo-selected", {
                mimeType: selected.type,
            });
        },
        [],
    );

    const handleScan = useCallback(async () => {
        if (!file || !regionId) {
            return;
        }
        if (!isSupportedMimeType(file.type)) {
            return;
        }
        handleEvent("scan-business-card-submit", { mimeType: file.type });
        setFileError(null);
        try {
            const dataBase64 = await readFileAsBase64(file);
            const result = await scanMutation.mutateAsync({
                regionId,
                mimeType: file.type,
                originalFilename: file.name,
                dataBase64,
            });
            if (result.ocrFields) {
                stashOcrFieldsForFeedback(result.referenceContactId, result.ocrFields);
            }
            navigate(`/references/contacts/show/${result.referenceContactId}`);
        } catch (err) {
            if (!scanMutation.error) {
                setFileError(
                    err instanceof Error
                        ? err.message
                        : "Could not read the selected image.",
                );
            }
        }
    }, [file, regionId, scanMutation]);

    const mutationErrorMessage = useMemo(() => {
        if (!scanMutation.error) {
            return null;
        }
        return (
            scanMutation.error.message ||
            "Something went wrong scanning the card. Please try again."
        );
    }, [scanMutation.error]);

    const isBusy = scanMutation.isPending;

    return (
        <Stage>
            <PageHeader title="Scan business card" />
            <Content>
                <div className="max-w-md mx-auto space-y-4">
                    <p className="text-sm text-zinc-600">
                        Take a photo of a business card or upload one from your device.
                        We&apos;ll read the contact info and create a new referral source
                        for you.
                    </p>

                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        className="hidden"
                        onChange={handleFileSelected}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleFileSelected}
                    />

                    <div className="flex flex-col sm:flex-row gap-2">
                        <PrimaryButton
                            onClick={() => cameraInputRef.current?.click()}
                            disabled={isBusy}>
                            <div className="flex items-center justify-center space-x-2">
                                <CameraIcon className="w-5 h-5" />
                                <span>Take photo</span>
                            </div>
                        </PrimaryButton>
                        <SecondaryButton
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isBusy}>
                            <div className="flex items-center justify-center space-x-2">
                                <PhotoIcon className="w-5 h-5" />
                                <span>Choose from device</span>
                            </div>
                        </SecondaryButton>
                    </div>

                    {fileError && <InlineBanner type="error">{fileError}</InlineBanner>}

                    {file && (
                        <PrimaryButton
                            onClick={handleScan}
                            disabled={isBusy || !regionId}>
                            {isBusy ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <Spinner />
                                    <span>Scanning...</span>
                                </div>
                            ) : (
                                <span>Scan this card</span>
                            )}
                        </PrimaryButton>
                    )}

                    {mutationErrorMessage && (
                        <InlineBanner type="error">{mutationErrorMessage}</InlineBanner>
                    )}

                    {previewUrl && (
                        <div className="border border-zinc-200 rounded-md overflow-hidden">
                            <img
                                src={previewUrl}
                                alt="Business card preview"
                                className="w-full h-auto block"
                            />
                        </div>
                    )}
                </div>
            </Content>
        </Stage>
    );
};
